import { useState, useCallback } from 'react'

export interface UseCopyToClipboardReturn {
  copy: (text: string) => Promise<void>
  copied: boolean
}

/**
 * Hook for copying text to clipboard with fallback support
 *
 * Features:
 * - Uses modern Clipboard API when available
 * - Falls back to document.execCommand for older browsers
 * - Auto-resets copied state after 2 seconds
 * - Handles all error cases silently
 */
export const useCopyToClipboard = (): UseCopyToClipboardReturn => {
  const [copied, setCopied] = useState(false)

  const copy = useCallback(async (text: string) => {
    try {
      // Try modern Clipboard API first
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback to execCommand for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
      } catch {
        // Silent fail if both methods fail
      }
    }
  }, [])

  return { copy, copied }
}
